import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as React from 'react';
import { forwardRef, useId, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { ChevronDown, X, Loader2, Pencil, Search, UserX, Check, Info, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cva } from 'class-variance-authority';
import { create } from 'zustand';

// src/components/avatar.tsx
var SIZES = {
  xs: { box: "size-4", text: "text-[8px]" },
  sm: { box: "size-5", text: "text-[9px]" },
  md: { box: "size-6", text: "text-[10px]" },
  lg: { box: "size-8", text: "text-[11px]" }
};
function initialsOf(name, email) {
  const source = name.trim() !== "" ? name.trim() : (email ?? "").trim();
  if (source === "") return "?";
  const words = source.split(/\s+/).filter((w) => w !== "");
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}
function Avatar({
  name,
  email,
  src,
  size = "sm",
  ring,
  className
}) {
  const { box, text } = SIZES[size];
  const ringClass = ring ? "ring-2 ring-[var(--color-accent-amber)]" : "";
  if (src != null && src !== "") {
    return /* @__PURE__ */ jsx(
      "img",
      {
        src,
        alt: "",
        "aria-hidden": "true",
        className: `${box} ${ringClass} shrink-0 rounded-full object-cover ${className ?? ""}`
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "span",
    {
      "aria-hidden": "true",
      className: `${box} ${text} ${ringClass} inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--color-canvas-deep)] font-bold text-[var(--color-text-strong)] ${className ?? ""}`,
      children: initialsOf(name, email)
    }
  );
}
var toneClass = {
  neutral: "bg-[var(--color-canvas-deep)] text-[var(--color-text-muted)] border-[var(--color-border-default)]",
  amber: "bg-[var(--color-amber-100)] text-[var(--color-amber-800)] border-[var(--color-amber-200)]",
  success: "bg-[rgb(17_122_67/0.08)] text-[var(--color-text-positive)] border-[rgb(17_122_67/0.25)]",
  warning: "bg-[rgb(217_119_6/0.08)] text-[var(--color-text-warning)] border-[rgb(217_119_6/0.25)]",
  danger: "bg-[rgb(220_38_38/0.08)] text-[var(--color-text-danger)] border-[rgb(220_38_38/0.25)]",
  info: "bg-[rgb(37_99_235/0.08)] text-[rgb(37_99_235)] border-[rgb(37_99_235/0.25)]",
  violet: "bg-[var(--color-violet-100)] text-[var(--color-violet-700)] border-[var(--color-violet-200)]",
  teal: "bg-[rgb(13_148_136/0.08)] text-[var(--color-accent-teal)] border-[rgb(13_148_136/0.25)]"
};
function Badge({
  tone = "neutral",
  compact,
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: `inline-flex items-center rounded-[var(--radius-xs)] border font-semibold uppercase tracking-wide ${compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"} ${toneClass[tone]} ${className ?? ""}`,
      ...props,
      children
    }
  );
}
var sizeClasses = {
  sm: "px-2.5 py-1 text-[12px] gap-1",
  md: "px-3.5 py-1.5 text-[13px] gap-1.5",
  lg: "px-5 py-2 text-[14px] gap-2"
};
var Button = forwardRef(
  function Button2({
    variant = "secondary",
    size = "md",
    isLoading,
    leftIcon,
    rightIcon,
    fullWidth,
    disabled,
    children,
    className,
    type = "button",
    ...props
  }, ref) {
    const base = `inline-flex items-center justify-center font-semibold rounded-[var(--radius-md)] disabled:cursor-not-allowed disabled:opacity-60 ${sizeClasses[size]} ${fullWidth ? "w-full" : ""}`;
    if (variant === "primary") {
      return /* @__PURE__ */ jsx(
        "button",
        {
          ref,
          type,
          disabled: disabled ?? isLoading,
          className: `relative inline-flex rounded-[var(--radius-md)] border-[1.5px] border-[var(--color-accent-amber-dark)] bg-[var(--color-accent-amber-dark)] text-[var(--color-on-accent)] no-underline transition-colors hover:transition-none disabled:cursor-not-allowed disabled:opacity-60 ${fullWidth ? "w-full" : ""} ${className ?? ""}`,
          ...props,
          children: /* @__PURE__ */ jsx(
            "span",
            {
              className: `flex w-full items-center justify-center -translate-y-[2px] rounded-[var(--radius-md)] border-[1.5px] border-[var(--color-accent-amber-dark)] bg-[var(--color-accent-amber)] transition-transform duration-150 hover:-translate-y-[3px] hover:bg-[var(--color-accent-amber-hover)] active:translate-y-[2px] active:scale-[0.99] ${sizeClasses[size]}`,
              style: { transitionTimingFunction: "var(--ease-teamblob)" },
              children: isLoading ? /* @__PURE__ */ jsx(Spinner, {}) : /* @__PURE__ */ jsxs(Fragment, { children: [
                leftIcon,
                children,
                rightIcon
              ] })
            }
          )
        }
      );
    }
    const variantClass = {
      secondary: "border-[1.5px] border-[var(--color-border-strong)] bg-transparent text-[var(--color-text-strong)] hover:bg-[var(--color-hover-overlay)]",
      ghost: "border-[1.5px] border-transparent bg-transparent text-[var(--color-text-body)] hover:bg-[var(--color-hover-overlay)]",
      destructive: "border-[1.5px] border-[var(--color-text-danger)]/40 bg-transparent text-[var(--color-text-danger)] hover:bg-[var(--color-text-danger)]/10",
      danger: "border-[1.5px] border-[var(--color-error-solid)] bg-[var(--color-error-solid)] text-white hover:bg-[var(--color-error-solid-hover)]"
    }[variant];
    return /* @__PURE__ */ jsx(
      "button",
      {
        ref,
        type,
        disabled: disabled ?? isLoading,
        className: `${base} ${variantClass} ${className ?? ""}`,
        ...props,
        children: isLoading ? /* @__PURE__ */ jsx(Spinner, {}) : /* @__PURE__ */ jsxs(Fragment, { children: [
          leftIcon,
          children,
          rightIcon
        ] })
      }
    );
  }
);
function Spinner() {
  return /* @__PURE__ */ jsx(
    "span",
    {
      "aria-hidden": "true",
      className: "size-3 animate-spin rounded-full border-[1.5px] border-current border-t-transparent"
    }
  );
}
function DataTable({
  columns,
  rows,
  rowKey = (r) => r.id ?? "",
  onRowClick,
  compact,
  ariaLabel
}) {
  return /* @__PURE__ */ jsx("div", { className: "overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border-subtle)]", children: /* @__PURE__ */ jsxs(
    "table",
    {
      className: "w-full border-collapse text-[13px]",
      "aria-label": ariaLabel,
      children: [
        /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsx("tr", { className: "bg-[var(--color-canvas)]", children: columns.map((col) => /* @__PURE__ */ jsx(
          "th",
          {
            scope: "col",
            style: col.width ? { width: col.width } : void 0,
            className: `px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-strong)] ${col.align === "end" ? "text-end" : col.align === "center" ? "text-center" : "text-start"}`,
            children: col.header
          },
          col.key
        )) }) }),
        /* @__PURE__ */ jsx("tbody", { children: rows.map((row) => /* @__PURE__ */ jsx(
          "tr",
          {
            onClick: onRowClick ? () => onRowClick(row) : void 0,
            className: `border-t border-[var(--color-border-subtle)] ${onRowClick ? "cursor-pointer hover:bg-[var(--color-canvas)]" : "hover:bg-[var(--color-canvas)]"}`,
            children: columns.map((col) => /* @__PURE__ */ jsx(
              "td",
              {
                className: `${compact ? "px-3 py-1.5" : "px-3 py-2.5"} ${col.align === "end" ? "text-end" : col.align === "center" ? "text-center" : "text-start"} text-[var(--color-text-body)]`,
                children: col.cell(row)
              },
              col.key
            ))
          },
          rowKey(row)
        )) })
      ]
    }
  ) });
}
var SR_ONLY_STYLE = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0
};
function Dialog({
  open,
  onOpenChange,
  title,
  description,
  srTitle,
  children,
  maxWidth = 520
}) {
  return /* @__PURE__ */ jsx(RadixDialog.Root, { open, onOpenChange, children: /* @__PURE__ */ jsxs(RadixDialog.Portal, { children: [
    /* @__PURE__ */ jsx(RadixDialog.Overlay, { className: "fixed inset-0 z-[var(--shell-z-modal)] bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" }),
    /* @__PURE__ */ jsxs(
      RadixDialog.Content,
      {
        className: "fixed start-1/2 top-1/2 z-[var(--shell-z-modal)] w-full -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--radius-window)] border border-[var(--color-border-default)] bg-[var(--color-window-face)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        style: {
          maxWidth: `${maxWidth}px`,
          boxShadow: "var(--shadow-window-focused)"
        },
        children: [
          title && /* @__PURE__ */ jsxs("header", { className: "flex items-center justify-between border-b border-[var(--color-border-subtle)] px-5 py-3", children: [
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx(RadixDialog.Title, { className: "m-0 text-[15px] font-bold text-[var(--color-text-strong)]", children: title }),
              description && /* @__PURE__ */ jsx(RadixDialog.Description, { className: "mt-0.5 text-[12px] text-[var(--color-text-muted)]", children: description })
            ] }),
            /* @__PURE__ */ jsx(RadixDialog.Close, { asChild: true, children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                "aria-label": "Close",
                className: "flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:bg-[var(--color-hover-overlay)] hover:text-[var(--color-text-strong)]",
                children: /* @__PURE__ */ jsx(X, { size: 14 })
              }
            ) })
          ] }),
          !title && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(RadixDialog.Title, { style: SR_ONLY_STYLE, children: srTitle ?? "Dialog" }),
            /* @__PURE__ */ jsx(RadixDialog.Close, { asChild: true, children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                "aria-label": "Close",
                className: "absolute end-3 top-3 z-10 flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:bg-[var(--color-hover-overlay)] hover:text-[var(--color-text-strong)]",
                children: /* @__PURE__ */ jsx(X, { size: 14 })
              }
            ) })
          ] }),
          children
        ]
      }
    )
  ] }) });
}
function DialogBody({
  children,
  className
}) {
  return /* @__PURE__ */ jsx("div", { className: `max-h-[60vh] overflow-auto px-5 py-4 ${className ?? ""}`, children });
}
function DialogFooter({ children }) {
  return /* @__PURE__ */ jsx("footer", { className: "flex items-center justify-end gap-2 border-t border-[var(--color-border-subtle)] px-5 py-3", children });
}
function DialogContent({
  children,
  className,
  style
}) {
  return /* @__PURE__ */ jsx("div", { className, style, children });
}
function DialogHeader({
  children,
  className
}) {
  return /* @__PURE__ */ jsx(
    "header",
    {
      className: `flex items-start justify-between border-b border-[var(--color-border-subtle)] px-5 py-3 ${className ?? ""}`,
      children: /* @__PURE__ */ jsx("div", { className: "flex-1", children })
    }
  );
}
function DialogTitle({
  children,
  className
}) {
  return /* @__PURE__ */ jsx(
    RadixDialog.Title,
    {
      className: `m-0 text-[15px] font-bold text-[var(--color-text-strong)] ${className ?? ""}`,
      children
    }
  );
}
function DialogDescription({
  children,
  className
}) {
  return /* @__PURE__ */ jsx(
    RadixDialog.Description,
    {
      className: `mt-0.5 text-[12px] text-[var(--color-text-muted)] ${className ?? ""}`,
      children
    }
  );
}
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function EditableField({
  value,
  onSave,
  type = "text",
  options,
  placeholder = "\u2014",
  format,
  className
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const [error, setError] = React.useState(null);
  const inputRef = React.useRef(null);
  const displayValue = React.useMemo(() => {
    if (format !== void 0) return format(value);
    if (value === null || value === "") return "";
    return String(value);
  }, [value, format]);
  function enterEdit() {
    setDraft(value !== null ? String(value) : "");
    setError(null);
    setIsEditing(true);
  }
  React.useEffect(() => {
    if (isEditing) {
      const id = window.setTimeout(() => {
        inputRef.current?.focus();
        if (inputRef.current instanceof HTMLInputElement) {
          inputRef.current.select();
        }
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [isEditing]);
  function cancel() {
    setIsEditing(false);
    setError(null);
  }
  async function save() {
    const trimmed = type === "date" ? draft : draft.trim();
    if (trimmed === (value !== null ? String(value) : "")) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save. Try again.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  }
  function handleKeyDown(e) {
    if (e.key === "Enter" && type !== "select") {
      e.preventDefault();
      void save();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  }
  function handleBlur() {
    void save();
  }
  if (isEditing) {
    const sharedInputClass = cn(
      "min-w-0 rounded-[var(--radius-sm)] border border-[var(--color-accent-amber)] bg-[var(--color-window-face)]",
      "px-1.5 py-0.5 text-[13px] font-medium text-[var(--color-text-strong)]",
      "outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-amber)]/40",
      "disabled:opacity-50 disabled:cursor-not-allowed"
    );
    return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-0.5", className), children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
        type === "select" && options !== void 0 ? /* @__PURE__ */ jsxs(
          "select",
          {
            ref: inputRef,
            value: draft,
            onChange: (e) => setDraft(e.target.value),
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            disabled: isSaving,
            className: cn(sharedInputClass, "pr-1"),
            children: [
              /* @__PURE__ */ jsx("option", { value: "", children: "\u2014" }),
              options.map((opt) => /* @__PURE__ */ jsx("option", { value: opt.value, children: opt.label }, opt.value))
            ]
          }
        ) : /* @__PURE__ */ jsx(
          "input",
          {
            ref: inputRef,
            type,
            value: draft,
            onChange: (e) => setDraft(e.target.value),
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            disabled: isSaving,
            placeholder,
            className: cn(sharedInputClass, "w-full")
          }
        ),
        isSaving && /* @__PURE__ */ jsx(
          Loader2,
          {
            "aria-label": "Saving\u2026",
            size: 12,
            className: "shrink-0 animate-spin text-[var(--color-text-muted)]"
          }
        )
      ] }),
      error !== null && /* @__PURE__ */ jsx(
        "p",
        {
          role: "alert",
          className: "text-[11px] text-[var(--color-text-danger)]",
          children: error
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick: enterEdit,
      title: "Click to edit",
      className: cn(
        "group flex w-full items-center gap-1 rounded-[var(--radius-sm)] text-start",
        "text-[13px] font-medium text-[var(--color-text-strong)]",
        "hover:bg-[var(--color-canvas)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-amber)]",
        "-mx-1 px-1 py-0.5 transition-colors",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("span", { className: cn(!displayValue && "text-[var(--color-text-muted)]"), children: displayValue || placeholder }),
        /* @__PURE__ */ jsx(
          Pencil,
          {
            "aria-hidden": "true",
            size: 10,
            className: "shrink-0 text-[var(--color-text-muted)] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          }
        )
      ]
    }
  );
}
EditableField.displayName = "EditableField";
function EmptyState({
  title,
  description,
  tileGradient,
  glyph,
  icon,
  action
}) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center rounded-[var(--radius-md)] border border-dashed border-[var(--color-border-default)] bg-[var(--color-canvas)] px-6 py-12 text-center", children: [
    icon ? /* @__PURE__ */ jsx("div", { className: "mb-3", children: icon }) : tileGradient ? /* @__PURE__ */ jsx(
      "div",
      {
        "aria-hidden": "true",
        className: "mb-3 flex size-12 items-center justify-center rounded-[var(--radius-tile)] text-[20px] font-bold text-white",
        style: { background: tileGradient, boxShadow: "var(--shadow-tile)" },
        children: glyph
      }
    ) : null,
    /* @__PURE__ */ jsx("h3", { className: "m-0 text-[15px] font-bold text-[var(--color-text-strong)]", children: title }),
    description && /* @__PURE__ */ jsx("p", { className: "mx-auto mt-1 max-w-[44ch] text-[12px] text-[var(--color-text-muted)]", children: description }),
    action && /* @__PURE__ */ jsx("div", { className: "mt-4", children: action })
  ] });
}
var Input = forwardRef(function Input2({
  label,
  helperText,
  error,
  startIcon,
  endIcon,
  compact,
  id,
  className,
  ...props
}, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hasError = Boolean(error);
  const describedBy = hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : void 0;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    label && /* @__PURE__ */ jsx(
      "label",
      {
        htmlFor: inputId,
        className: "text-[12px] font-semibold text-[var(--color-text-strong)]",
        children: label
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "relative flex items-center", children: [
      startIcon && /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": "true",
          className: "pointer-events-none absolute start-2.5 flex items-center text-[var(--color-text-subtle)]",
          children: startIcon
        }
      ),
      /* @__PURE__ */ jsx(
        "input",
        {
          ref,
          id: inputId,
          "aria-invalid": hasError,
          "aria-describedby": describedBy,
          className: `w-full rounded-[var(--radius-md)] border bg-[var(--color-window-face)] text-[var(--color-text-strong)] outline-none placeholder:text-[var(--color-text-subtle)] ${compact ? "py-1 text-[12px]" : "py-2 text-[13px]"} ${startIcon ? "ps-8" : "ps-3"} ${endIcon ? "pe-8" : "pe-3"} ${hasError ? "border-[var(--color-text-danger)] focus:border-[var(--color-text-danger)]" : "border-[var(--color-border-default)] hover:border-[var(--color-border-strong)] focus:border-[var(--color-accent-amber-dark)]"} ${className ?? ""}`,
          ...props
        }
      ),
      endIcon && /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": "true",
          className: "absolute end-2.5 flex items-center text-[var(--color-text-subtle)]",
          children: endIcon
        }
      )
    ] }),
    hasError && /* @__PURE__ */ jsx(
      "p",
      {
        id: `${inputId}-error`,
        role: "alert",
        className: "text-[11px] text-[var(--color-text-danger)]",
        children: error
      }
    ),
    !hasError && helperText && /* @__PURE__ */ jsx(
      "p",
      {
        id: `${inputId}-helper`,
        className: "text-[11px] text-[var(--color-text-muted)]",
        children: helperText
      }
    )
  ] });
});
function firstEnabled(options) {
  const i = options.findIndex((o) => o.disabled !== true);
  return i >= 0 ? i : 0;
}
function matches(option, query) {
  if (query === "") return true;
  const q = query.toLowerCase();
  return option.label.toLowerCase().includes(q) || (option.searchText ?? "").toLowerCase().includes(q);
}
function Listbox({
  options,
  value,
  onChange,
  renderOption,
  renderValue,
  label,
  searchable = true,
  searchPlaceholder = "Search\u2026",
  searchThreshold = 4,
  placeholder = "Select\u2026",
  emptyMessage = "No matches.",
  disabled,
  error,
  helperText,
  compact,
  className,
  menuWidth,
  "aria-label": ariaLabel
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const baseId = useId();
  const listId = `${baseId}-list`;
  const labelId = `${baseId}-label`;
  const hasError = Boolean(error);
  const showSearch = searchable && options.length >= searchThreshold;
  const visible = useMemo(
    () => options.filter((o) => matches(o, query)),
    [options, query]
  );
  const selected = options.find((o) => o.value === value);
  const commit = useCallback(
    (option) => {
      if (!option || option.disabled === true) return;
      onChange(option.value);
      setOpen(false);
      setQuery("");
      triggerRef.current?.focus();
    },
    [onChange]
  );
  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);
  useEffect(() => {
    if (!open) return;
    const index = visible.findIndex((o) => o.value === value);
    setActiveIndex(index >= 0 ? index : firstEnabled(visible));
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setDropUp(window.innerHeight - rect.bottom < 260);
    if (showSearch) {
      const raf = requestAnimationFrame(() => searchRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
    return void 0;
  }, [open]);
  useEffect(() => {
    if (activeIndex >= visible.length) setActiveIndex(0);
  }, [visible.length, activeIndex]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);
  useEffect(() => {
    if (!open || !listRef.current) return;
    const row = listRef.current.querySelector(
      `[data-index="${String(activeIndex)}"]`
    );
    row?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);
  const move = useCallback(
    (delta) => {
      if (visible.length === 0) return;
      setActiveIndex((current) => {
        let next = current;
        for (let i = 0; i < visible.length; i += 1) {
          next = (next + delta + visible.length) % visible.length;
          if (visible[next]?.disabled !== true) return next;
        }
        return current;
      });
    },
    [visible]
  );
  const onKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) setOpen(true);
        else move(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) setOpen(true);
        else move(-1);
        break;
      case "Home":
        if (open) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case "End":
        if (open) {
          e.preventDefault();
          setActiveIndex(Math.max(0, visible.length - 1));
        }
        break;
      case "Enter":
        if (open) {
          e.preventDefault();
          commit(visible[activeIndex]);
        } else {
          e.preventDefault();
          setOpen(true);
        }
        break;
      case " ":
        if (!open) {
          e.preventDefault();
          setOpen(true);
        }
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          close();
          triggerRef.current?.focus();
        }
        break;
      case "Tab":
        if (open) close();
        break;
    }
  };
  const activeOptionId = visible[activeIndex] ? `${baseId}-opt-${String(activeIndex)}` : void 0;
  const triggerLabel = renderValue ? renderValue(selected) : selected ? /* @__PURE__ */ jsx("span", { className: "truncate", children: selected.label }) : /* @__PURE__ */ jsx("span", { className: "truncate text-[var(--color-text-subtle)]", children: placeholder });
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    label != null && label !== "" && /* @__PURE__ */ jsx(
      "span",
      {
        id: labelId,
        className: "text-[12px] font-semibold text-[var(--color-text-strong)]",
        children: label
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "relative", ref: rootRef, onKeyDown, children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          ref: triggerRef,
          type: "button",
          role: "combobox",
          "aria-expanded": open,
          "aria-controls": open ? listId : void 0,
          "aria-haspopup": "listbox",
          "aria-invalid": hasError,
          ...open && !showSearch && activeOptionId != null ? { "aria-activedescendant": activeOptionId } : {},
          ...ariaLabel != null ? { "aria-label": ariaLabel } : label != null && label !== "" ? { "aria-labelledby": labelId } : {},
          disabled,
          onClick: () => setOpen((v) => !v),
          className: `flex w-full cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border bg-[var(--color-window-face)] pe-8 text-start text-[var(--color-text-strong)] outline-none disabled:cursor-not-allowed disabled:opacity-60 ${compact === true ? "py-1 ps-2 text-[12px]" : "py-2 ps-3 text-[13px]"} ${hasError ? "border-[var(--color-text-danger)]" : open ? "border-[var(--color-accent-amber-dark)]" : "border-[var(--color-border-default)] hover:border-[var(--color-border-strong)]"} ${className ?? ""}`,
          children: [
            /* @__PURE__ */ jsx("span", { className: "flex min-w-0 flex-1 items-center gap-2", children: triggerLabel }),
            /* @__PURE__ */ jsx(
              ChevronDown,
              {
                size: 14,
                "aria-hidden": "true",
                className: `pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-subtle)] transition-transform ${open ? "rotate-180" : ""}`
              }
            )
          ]
        }
      ),
      open && /* @__PURE__ */ jsxs(
        "div",
        {
          className: `absolute z-50 rounded-[var(--radius-md)] border border-[var(--color-border-soft)] bg-[var(--color-window-face)] py-1 shadow-lg ${dropUp ? "bottom-full mb-1" : "top-full mt-1"} ${menuWidth ?? "w-full min-w-[220px]"}`,
          children: [
            showSearch && /* @__PURE__ */ jsxs("div", { className: "relative border-b border-[var(--color-border-soft)] px-2 pb-1.5 pt-1", children: [
              /* @__PURE__ */ jsx(
                Search,
                {
                  size: 12,
                  "aria-hidden": "true",
                  className: "pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-[var(--color-text-subtle)]"
                }
              ),
              /* @__PURE__ */ jsx(
                "input",
                {
                  ref: searchRef,
                  type: "text",
                  value: query,
                  onChange: (e) => {
                    setQuery(e.target.value);
                    setActiveIndex(firstEnabled(visible));
                  },
                  placeholder: searchPlaceholder,
                  "aria-label": searchPlaceholder,
                  role: "combobox",
                  "aria-expanded": true,
                  "aria-autocomplete": "list",
                  "aria-controls": listId,
                  ...activeOptionId != null ? { "aria-activedescendant": activeOptionId } : {},
                  className: "w-full rounded-[var(--radius-sm)] border border-transparent bg-[var(--color-canvas-deep)] py-1 pe-2 ps-6 text-[12px] text-[var(--color-text-strong)] outline-none placeholder:text-[var(--color-text-subtle)] focus:border-[var(--color-accent-amber-dark)]"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs(
              "div",
              {
                ref: listRef,
                id: listId,
                role: "listbox",
                "aria-label": ariaLabel ?? label ?? "Options",
                className: "max-h-56 overflow-y-auto",
                children: [
                  visible.length === 0 && /* @__PURE__ */ jsx("div", { className: "px-3 py-4 text-center text-[11px] text-[var(--color-text-muted)]", children: emptyMessage }),
                  visible.map((option, index) => {
                    const isSelected = option.value === value;
                    const isActive = index === activeIndex;
                    return /* @__PURE__ */ jsx(
                      "div",
                      {
                        id: `${baseId}-opt-${String(index)}`,
                        "data-index": index,
                        role: "option",
                        "aria-selected": isSelected,
                        "aria-disabled": option.disabled === true,
                        onClick: () => commit(option),
                        onMouseEnter: () => setActiveIndex(index),
                        className: `flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-[13px] ${option.disabled === true ? "cursor-not-allowed opacity-50" : isActive ? "bg-[var(--color-accent-amber)]/15" : isSelected ? "bg-[var(--color-accent-amber)]/8" : ""}`,
                        children: renderOption(option, isSelected)
                      },
                      option.value
                    );
                  })
                ]
              }
            )
          ]
        }
      )
    ] }),
    hasError && /* @__PURE__ */ jsx("p", { role: "alert", className: "text-[11px] text-[var(--color-text-danger)]", children: error }),
    !hasError && helperText != null && helperText !== "" && /* @__PURE__ */ jsx("p", { className: "text-[11px] text-[var(--color-text-muted)]", children: helperText })
  ] });
}

// src/components/text-direction.ts
var RTL_LETTER = /(?=\p{L})[\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Syriac}\p{Script=Thaana}]/u;
var LTR_LETTER = /(?![\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Syriac}\p{Script=Thaana}])\p{L}/u;
var RTL_LETTERS = new RegExp(RTL_LETTER.source, "gu");
var LTR_LETTERS = new RegExp(LTR_LETTER.source, "gu");
var LINK_TARGET = /\]\([^)]*\)/g;
function letters(text) {
  const visible = text.replace(LINK_TARGET, "]");
  return {
    rtl: visible.match(RTL_LETTERS)?.length ?? 0,
    ltr: visible.match(LTR_LETTERS)?.length ?? 0
  };
}
function hasRtlLetters(text) {
  return RTL_LETTER.test(text);
}
var ARABIC_FRAME = new Set(
  [
    "\u0641\u064A \u0641\u0649 \u0645\u0646 \u0639\u0644\u0649 \u0639\u0646 \u0645\u0639 \u0627\u0644\u0649 \u0627\u0644\u064A \u0648 \u0627\u0648 \u062B\u0645 \u0644\u0643\u0646 \u0628\u0633 \u0628\u064A\u0646 \u062D\u062A\u0649 \u0644\u062D\u062F \u0632\u064A \u0645\u062B\u0644 \u063A\u064A\u0631",
    "\u0647\u0648 \u0647\u064A \u0647\u0645 \u0647\u0645\u0627 \u0627\u0646\u0627 \u0627\u0646\u062A \u0627\u0646\u062A\u064A \u0627\u0646\u062A\u0648 \u0627\u062D\u0646\u0627 \u062F\u0647 \u062F\u064A \u062F\u0627 \u062F\u0648\u0644 \u0643\u062F\u0647 \u0643\u062F\u0627",
    "\u0627\u0644\u0644\u064A \u0627\u0644\u0630\u064A \u0627\u0644\u062A\u064A \u0627\u0644\u0630\u064A\u0646 \u0647\u0630\u0627 \u0647\u0630\u0647 \u0630\u0644\u0643 \u062A\u0644\u0643 \u0643\u0627\u0646 \u0643\u0627\u0646\u062A \u064A\u0643\u0648\u0646 \u0627\u0646 \u0644\u0648 \u0627\u0630\u0627",
    "\u064A\u0639\u0646\u064A \u0639\u0634\u0627\u0646 \u0639\u0644\u0634\u0627\u0646 \u0645\u0634 \u0645\u0641\u064A\u0634 \u0641\u064A\u0647 \u0641\u064A\u0647\u0627 \u0641\u064A\u0647\u0645 \u0639\u0646\u062F \u0639\u0646\u062F\u064A \u0639\u0646\u062F\u0643 \u0639\u0646\u062F\u0646\u0627",
    "\u0644\u064A\u0647 \u0627\u0632\u0627\u064A \u0627\u0645\u062A\u0649 \u0641\u064A\u0646 \u0627\u064A\u0647 \u0645\u064A\u0646 \u0643\u0644 \u0643\u0644\u0647 \u0643\u0644\u0647\u0627 \u0643\u0644\u0647\u0645 \u0644\u0633\u0647 \u062E\u0644\u0627\u0635 \u0644\u0627 \u0645\u0627 \u0648\u0644\u0627",
    "\u0628\u062A\u0627\u0639 \u0628\u062A\u0627\u0639\u0647 \u0628\u062A\u0627\u0639\u0629 \u0628\u062A\u0627\u0639\u062A \u0628\u062A\u0648\u0639 \u0643\u0645\u0627\u0646 \u0628\u0631\u0636\u0647 \u062C\u062F\u0627 \u0627\u0643\u062A\u0631 \u0627\u0642\u0644 \u0628\u0639\u062F \u0642\u0628\u0644"
  ].join(" ").split(" ")
);
var ENGLISH_FRAME = new Set(
  [
    "the a an of to in on at for with from by and or but nor so as if",
    "is are was were be been being has have had do does did not no",
    "it its this that these those there here i me my we us our you your",
    "he him his she her they them their who whom which what when where",
    "why how will would can could should shall may might must about into",
    "than then also just only very more most some any all each every both"
  ].join(" ").split(" ")
);
var WORD_LETTERS = /[\p{L}\p{M}]+/gu;
var ARABIC_MARKS = /[\u064B-\u065F\u0670\u0640]/g;
var ALEF_FORMS = /[أإآ]/g;
function frameDirection(text) {
  let arabic = 0;
  let english = 0;
  for (const [word] of text.matchAll(WORD_LETTERS)) {
    if (ARABIC_FRAME.has(word.replace(ARABIC_MARKS, "").replace(ALEF_FORMS, "\u0627")))
      arabic += 1;
    else if (ENGLISH_FRAME.has(word.toLowerCase())) english += 1;
  }
  if (arabic === english) return void 0;
  return arabic > english ? "rtl" : "ltr";
}
function textDirection(text) {
  const visible = text.replace(LINK_TARGET, "]");
  const { rtl, ltr } = letters(visible);
  if (rtl === 0 && ltr === 0) return void 0;
  if (rtl === 0) return "ltr";
  if (ltr === 0) return "rtl";
  return frameDirection(visible) ?? (rtl >= ltr ? "rtl" : "ltr");
}
function blockDirection(text, base) {
  const { rtl, ltr } = letters(text);
  if (rtl === 0) return ltr > 0 ? "ltr" : base;
  if (base === "rtl") return "rtl";
  return textDirection(text);
}
var LRI = "\u2066";
var RLI = "\u2067";
var PDI = "\u2069";
var BIDI_CONTROL = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/;
var BIDI_CONTROLS = new RegExp(BIDI_CONTROL.source, "g");
function hasBidiControls(text) {
  return BIDI_CONTROL.test(text);
}
function stripBidiControls(text) {
  return text.replace(BIDI_CONTROLS, "");
}
var WORD = /\S+/gu;
var WORD_PARTS = /^([^\p{L}\p{N}@#]*)(.*?)[^\p{L}\p{N}\p{M}]*$/su;
var RTL_CHARACTER = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/u;
function oppositeRuns(text, dir) {
  const opposite = dir === "rtl" ? LTR_LETTER : RTL_LETTER;
  const same = dir === "rtl" ? RTL_LETTER : LTR_LETTER;
  const runs = [];
  let run = null;
  for (const word of text.matchAll(WORD)) {
    const [, lead = "", core = ""] = WORD_PARTS.exec(word[0]) ?? [];
    const start = word.index + lead.length;
    const end = start + core.length;
    if (same.test(core)) {
      if (run !== null) runs.push(run);
      run = null;
    } else if (opposite.test(core)) {
      const gap = run === null ? "" : text.slice(run[1], start);
      if (run !== null && !gap.includes("\n") && !(dir === "rtl" && RTL_CHARACTER.test(gap))) {
        run[1] = end;
      } else {
        if (run !== null) runs.push(run);
        run = [start, end];
      }
    }
  }
  if (run !== null) runs.push(run);
  return runs;
}
function isolateRuns(text, dir) {
  const runs = oppositeRuns(text, dir);
  if (runs.length === 0) return text;
  const open = dir === "rtl" ? LRI : RLI;
  let out = "";
  let last = 0;
  for (const [start, end] of runs) {
    out += `${text.slice(last, start)}${open}${text.slice(start, end)}${PDI}`;
    last = end;
  }
  return out + text.slice(last);
}
function isolateText(text, context, whole = true) {
  const base = whole ? textDirection(text) ?? context : context;
  return text.split("\n").map((line) => {
    if (line.trim() === "") return line;
    const dir = whole ? blockDirection(line, base) ?? base : context;
    const sealed = isolateRuns(line, dir);
    if (dir === context) return sealed;
    return `${dir === "rtl" ? RLI : LRI}${sealed}${PDI}`;
  }).join("\n");
}

// src/components/markdown-bidi.ts
var DIRECTED = /* @__PURE__ */ new Set([
  ...["p", "h1", "h2", "h3", "h4", "h5", "h6"],
  ...["ul", "ol", "li", "blockquote", "th", "td"]
]);
var WITH_TEXT = /* @__PURE__ */ new Set([
  ...["p", "h1", "h2", "h3", "h4", "h5", "h6"],
  ...["li", "th", "td"]
]);
var BREAKS = /* @__PURE__ */ new Set([
  ...DIRECTED,
  ...["pre", "table", "thead", "tbody", "tr", "hr", "div", "br"]
]);
var NEUTRAL = "\uFFFC";
var isElement = (n) => n.type === "element";
var isText = (n) => n.type === "text";
function textOf(node) {
  if (isText(node)) return node.value;
  return (node.children ?? []).map(textOf).join("");
}
function sealLine(nodes, dir) {
  const text = nodes.map((n) => n === null ? NEUTRAL : n.value).join("");
  const runs = oppositeRuns(text, dir);
  if (runs.length === 0) return;
  const open = dir === "rtl" ? LRI : RLI;
  const inserts = /* @__PURE__ */ new Map();
  const at = (offset, mark, end) => {
    let from = 0;
    for (const node of nodes) {
      const length = node === null ? 1 : node.value.length;
      const inside = end ? offset > from && offset <= from + length : offset >= from && offset < from + length;
      if (inside && node !== null) {
        const list = inserts.get(node) ?? [];
        list.push([offset - from, mark]);
        inserts.set(node, list);
        return;
      }
      from += length;
    }
  };
  for (const [start, end] of runs) {
    at(start, open, false);
    at(end, PDI, true);
  }
  for (const [node, list] of inserts) {
    let value = node.value;
    for (const [offset, mark] of list.sort((a, b) => b[0] - a[0])) {
      value = value.slice(0, offset) + mark + value.slice(offset);
    }
    node.value = value;
  }
}
function sealBlock(block, dir) {
  let line = [];
  const walk = (children) => {
    for (const child of children) {
      if (isText(child)) line.push(child);
      else if (!isElement(child)) continue;
      else if (BREAKS.has(child.tagName)) {
        sealLine(line, dir);
        line = [];
      } else if (child.tagName === "code") line.push(null);
      else walk(child.children);
    }
  };
  walk(block.children);
  sealLine(line, dir);
}
function visit(node, base, inherited) {
  if (isText(node)) return;
  for (const child of node.children ?? []) {
    if (!isElement(child)) continue;
    child.properties ??= {};
    if (child.tagName === "pre" || child.tagName === "code") {
      child.properties.dir = "ltr";
      continue;
    }
    let passed;
    if (DIRECTED.has(child.tagName)) {
      const isList = child.tagName === "ul" || child.tagName === "ol";
      const dir = inherited ?? (isList && base === "rtl" ? "rtl" : blockDirection(textOf(child), base));
      child.properties.dir = dir ?? "auto";
      if (dir !== void 0 && WITH_TEXT.has(child.tagName)) {
        sealBlock(child, dir);
      }
      if (isList || child.tagName === "li") passed = dir;
    }
    visit(child, base, passed);
  }
}
function rehypeBidi(options) {
  return (tree) => visit(tree, options.base);
}
function Markdown({
  children,
  className = ""
}) {
  const base = textDirection(children);
  return /* @__PURE__ */ jsx("div", { dir: base, "data-bidi": "off", className: `prose-sm ${className}`, children: /* @__PURE__ */ jsx(
    ReactMarkdown,
    {
      remarkPlugins: [remarkGfm],
      rehypePlugins: [[rehypeBidi, { base }]],
      components: {
        p: ({ dir, children: c }) => /* @__PURE__ */ jsx("p", { dir, className: "mb-1.5 last:mb-0", children: c }),
        h1: ({ dir, children: c }) => /* @__PURE__ */ jsx("h1", { dir, children: c }),
        h2: ({ dir, children: c }) => /* @__PURE__ */ jsx("h2", { dir, children: c }),
        h3: ({ dir, children: c }) => /* @__PURE__ */ jsx("h3", { dir, children: c }),
        h4: ({ dir, children: c }) => /* @__PURE__ */ jsx("h4", { dir, children: c }),
        ul: ({ dir, children: c }) => /* @__PURE__ */ jsx("ul", { dir, className: "mb-1.5 list-disc ps-4", children: c }),
        ol: ({ dir, children: c }) => /* @__PURE__ */ jsx("ol", { dir, className: "mb-1.5 list-decimal ps-4", children: c }),
        li: ({ dir, children: c }) => /* @__PURE__ */ jsx("li", { dir, className: "mb-0.5", children: c }),
        a: ({ href, children: c }) => /* @__PURE__ */ jsx(
          "a",
          {
            href,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "text-[var(--color-accent-amber)] underline hover:text-[var(--color-accent-amber-hover)]",
            children: c
          }
        ),
        strong: ({ children: c }) => /* @__PURE__ */ jsx("strong", { className: "font-semibold", children: c }),
        code: ({ dir, children: c }) => /* @__PURE__ */ jsx(
          "code",
          {
            dir,
            className: "rounded bg-[var(--color-canvas)] px-1 py-0.5 text-[0.9em] font-mono",
            children: c
          }
        ),
        pre: ({ dir, children: c }) => /* @__PURE__ */ jsx(
          "pre",
          {
            dir,
            className: "mb-1.5 overflow-x-auto rounded-[var(--radius-sm)] bg-[var(--color-canvas)] p-2 text-[0.85em]",
            children: c
          }
        ),
        blockquote: ({ dir, children: c }) => /* @__PURE__ */ jsx(
          "blockquote",
          {
            dir,
            className: "mb-1.5 border-s-2 border-[var(--color-border-strong)] ps-3 text-[var(--color-text-muted)]",
            children: c
          }
        ),
        hr: () => /* @__PURE__ */ jsx("hr", { className: "my-2 border-[var(--color-border-subtle)]" }),
        table: ({ children: c }) => /* @__PURE__ */ jsx("div", { className: "mb-1.5 overflow-x-auto", children: /* @__PURE__ */ jsx("table", { className: "w-full border-collapse text-[0.9em]", children: c }) }),
        th: ({ dir, children: c }) => /* @__PURE__ */ jsx(
          "th",
          {
            dir,
            className: "border border-[var(--color-border-subtle)] bg-[var(--color-canvas)] px-2 py-1 text-start font-semibold",
            children: c
          }
        ),
        td: ({ dir, children: c }) => /* @__PURE__ */ jsx(
          "td",
          {
            dir,
            className: "border border-[var(--color-border-subtle)] px-2 py-1",
            children: c
          }
        ),
        input: ({ checked, ...props }) => /* @__PURE__ */ jsx(
          "input",
          {
            type: "checkbox",
            checked,
            disabled: true,
            className: "me-1.5 accent-[var(--color-accent-amber)]",
            ...props
          }
        )
      },
      children
    }
  ) });
}
function PersonSelect({
  people,
  value,
  onChange,
  label,
  allowEmpty = true,
  emptyLabel = "Unassigned",
  placeholder,
  noPeopleMessage = "No people available.",
  disabled,
  error,
  helperText,
  compact,
  className,
  "aria-label": ariaLabel
}) {
  const options = useMemo(() => {
    const nameCounts = /* @__PURE__ */ new Map();
    for (const p of people) {
      const key = p.name.trim().toLowerCase();
      if (key !== "") nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
    }
    const rows = people.map((p) => {
      const hasName = p.name.trim() !== "";
      const email = p.email ?? "";
      const isAmbiguous = hasName && (nameCounts.get(p.name.trim().toLowerCase()) ?? 0) > 1;
      const display = hasName ? p.name : email !== "" ? email.split("@")[0] ?? email : "Unknown user";
      return {
        value: p.userId,
        label: display,
        searchText: email,
        ...p.disabled === true ? { disabled: true } : {},
        data: {
          person: p,
          showEmail: email !== "" && (!hasName || isAmbiguous)
        }
      };
    });
    if (allowEmpty) {
      rows.unshift({
        value: "",
        label: emptyLabel,
        data: { person: null, showEmail: false }
      });
    }
    return rows;
  }, [people, allowEmpty, emptyLabel]);
  return /* @__PURE__ */ jsx(
    Listbox,
    {
      options,
      value,
      onChange,
      searchPlaceholder: "Search people\u2026",
      emptyMessage: people.length === 0 ? noPeopleMessage : "No matches.",
      placeholder: placeholder ?? emptyLabel,
      ...label != null ? { label } : {},
      ...disabled != null ? { disabled } : {},
      ...error != null ? { error } : {},
      ...helperText != null ? { helperText } : {},
      ...compact != null ? { compact } : {},
      ...className != null ? { className } : {},
      ...ariaLabel != null ? { "aria-label": ariaLabel } : {},
      renderValue: (option) => {
        if (!option) {
          return /* @__PURE__ */ jsx("span", { className: "truncate text-[var(--color-text-subtle)]", children: placeholder ?? emptyLabel });
        }
        const person = option.data?.person ?? null;
        if (!person) {
          return /* @__PURE__ */ jsx("span", { className: "truncate text-[var(--color-text-muted)]", children: option.label });
        }
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            Avatar,
            {
              name: person.name,
              email: person.email,
              src: person.avatarUrl,
              size: compact === true ? "xs" : "sm"
            }
          ),
          /* @__PURE__ */ jsx("span", { className: "truncate", children: option.label })
        ] });
      },
      renderOption: (option, selected) => {
        const person = option.data?.person ?? null;
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          person ? /* @__PURE__ */ jsx(
            Avatar,
            {
              name: person.name,
              email: person.email,
              src: person.avatarUrl,
              size: "md"
            }
          ) : /* @__PURE__ */ jsx(
            "span",
            {
              "aria-hidden": "true",
              className: "inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-canvas-deep)] text-[var(--color-text-subtle)]",
              children: /* @__PURE__ */ jsx(UserX, { size: 12 })
            }
          ),
          /* @__PURE__ */ jsxs("span", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx(
              "span",
              {
                className: `block truncate ${person ? "text-[var(--color-text-strong)]" : "text-[var(--color-text-muted)]"}`,
                children: option.label
              }
            ),
            option.data?.showEmail === true && /* @__PURE__ */ jsx("span", { className: "block truncate text-[11px] text-[var(--color-text-muted)]", children: person?.email })
          ] }),
          selected && /* @__PURE__ */ jsx(
            Check,
            {
              size: 14,
              "aria-hidden": "true",
              className: "shrink-0 text-[var(--color-accent-amber-dark)]"
            }
          )
        ] });
      }
    }
  );
}
function Leading({ option }) {
  if (option.icon != null) {
    return /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": "true",
        className: "flex size-4 shrink-0 items-center justify-center text-[var(--color-text-muted)]",
        children: option.icon
      }
    );
  }
  if (option.color != null && option.color !== "") {
    return /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": "true",
        style: { backgroundColor: option.color },
        className: "size-2.5 shrink-0 rounded-full"
      }
    );
  }
  return null;
}
function PickerSelect({
  options,
  value,
  onChange,
  label,
  placeholder = "Select\u2026",
  emptyMessage,
  searchable,
  disabled,
  error,
  helperText,
  compact,
  className,
  "aria-label": ariaLabel
}) {
  const listOptions = useMemo(
    () => options.map((o) => ({
      value: o.value,
      label: o.label,
      searchText: o.description,
      ...o.disabled === true ? { disabled: true } : {},
      data: o
    })),
    [options]
  );
  return /* @__PURE__ */ jsx(
    Listbox,
    {
      options: listOptions,
      value,
      onChange,
      placeholder,
      searchPlaceholder: "Search\u2026",
      ...searchable != null ? { searchable } : {},
      ...emptyMessage != null ? { emptyMessage } : {},
      ...label != null ? { label } : {},
      ...disabled != null ? { disabled } : {},
      ...error != null ? { error } : {},
      ...helperText != null ? { helperText } : {},
      ...compact != null ? { compact } : {},
      ...className != null ? { className } : {},
      ...ariaLabel != null ? { "aria-label": ariaLabel } : {},
      renderValue: (option) => {
        if (!option) {
          return /* @__PURE__ */ jsx("span", { className: "truncate text-[var(--color-text-subtle)]", children: placeholder });
        }
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          option.data && /* @__PURE__ */ jsx(Leading, { option: option.data }),
          /* @__PURE__ */ jsx("span", { className: "truncate", children: option.label })
        ] });
      },
      renderOption: (option, selected) => /* @__PURE__ */ jsxs(Fragment, { children: [
        option.data && /* @__PURE__ */ jsx(Leading, { option: option.data }),
        /* @__PURE__ */ jsxs("span", { className: "min-w-0 flex-1", children: [
          /* @__PURE__ */ jsx("span", { className: "block truncate text-[var(--color-text-strong)]", children: option.label }),
          option.data?.description != null && option.data.description !== "" && /* @__PURE__ */ jsx("span", { className: "block truncate text-[11px] text-[var(--color-text-muted)]", children: option.data.description })
        ] }),
        selected && /* @__PURE__ */ jsx(
          Check,
          {
            size: 14,
            "aria-hidden": "true",
            className: "shrink-0 text-[var(--color-accent-amber-dark)]"
          }
        )
      ] })
    }
  );
}
var Select = forwardRef(
  function Select2({ label, error, helperText, compact, id, className, children, ...props }, ref) {
    const autoId = useId();
    const sid = id ?? autoId;
    const hasError = Boolean(error);
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      label && /* @__PURE__ */ jsx(
        "label",
        {
          htmlFor: sid,
          className: "text-[12px] font-semibold text-[var(--color-text-strong)]",
          children: label
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsx(
          "select",
          {
            ref,
            id: sid,
            "aria-invalid": hasError,
            className: `w-full cursor-pointer appearance-none rounded-[var(--radius-md)] border bg-[var(--color-window-face)] pe-8 text-[var(--color-text-strong)] outline-none ${compact ? "py-1 text-[12px] ps-2" : "py-2 text-[13px] ps-3"} ${hasError ? "border-[var(--color-text-danger)] focus:border-[var(--color-text-danger)]" : "border-[var(--color-border-default)] hover:border-[var(--color-border-strong)] focus:border-[var(--color-accent-amber-dark)]"} ${className ?? ""}`,
            ...props,
            children
          }
        ),
        /* @__PURE__ */ jsx(
          ChevronDown,
          {
            size: 14,
            "aria-hidden": "true",
            className: "pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-subtle)]"
          }
        )
      ] }),
      hasError && /* @__PURE__ */ jsx(
        "p",
        {
          role: "alert",
          className: "text-[11px] text-[var(--color-text-danger)]",
          children: error
        }
      ),
      !hasError && helperText && /* @__PURE__ */ jsx("p", { className: "text-[11px] text-[var(--color-text-muted)]", children: helperText })
    ] });
  }
);
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "aria-hidden": "true",
      className: `animate-pulse rounded-[var(--radius-xs)] bg-[var(--color-canvas-deep)] ${className ?? ""}`,
      ...props
    }
  );
}
function SkeletonTable({
  rows = 5,
  columns = 4
}) {
  return /* @__PURE__ */ jsxs("div", { className: "overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border-subtle)]", children: [
    /* @__PURE__ */ jsx("div", { className: "flex gap-4 bg-[var(--color-canvas)] px-3 py-2", children: Array.from({ length: columns }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-3 flex-1" }, i)) }),
    Array.from({ length: rows }).map((_, r) => /* @__PURE__ */ jsx(
      "div",
      {
        className: "flex items-center gap-4 border-t border-[var(--color-border-subtle)] px-3 py-3",
        children: Array.from({ length: columns }).map((_2, c) => /* @__PURE__ */ jsx(Skeleton, { className: "h-3 flex-1" }, c))
      },
      r
    ))
  ] });
}
var Textarea = forwardRef(
  function Textarea2({ label, helperText, error, id, className, ...props }, ref) {
    const autoId = useId();
    const tid = id ?? autoId;
    const hasError = Boolean(error);
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      label && /* @__PURE__ */ jsx(
        "label",
        {
          htmlFor: tid,
          className: "text-[12px] font-semibold text-[var(--color-text-strong)]",
          children: label
        }
      ),
      /* @__PURE__ */ jsx(
        "textarea",
        {
          ref,
          id: tid,
          "aria-invalid": hasError,
          className: `min-h-[80px] w-full resize-y rounded-[var(--radius-md)] border bg-[var(--color-window-face)] px-3 py-2 text-[13px] text-[var(--color-text-strong)] outline-none placeholder:text-[var(--color-text-subtle)] ${hasError ? "border-[var(--color-text-danger)] focus:border-[var(--color-text-danger)]" : "border-[var(--color-border-default)] hover:border-[var(--color-border-strong)] focus:border-[var(--color-accent-amber-dark)]"} ${className ?? ""}`,
          ...props
        }
      ),
      hasError && /* @__PURE__ */ jsx(
        "p",
        {
          role: "alert",
          className: "text-[11px] text-[var(--color-text-danger)]",
          children: error
        }
      ),
      !hasError && helperText && /* @__PURE__ */ jsx("p", { className: "text-[11px] text-[var(--color-text-muted)]", children: helperText })
    ] });
  }
);
var MAX_TOASTS = 5;
var DEFAULT_DURATION = 5e3;
var useToastStore = create((set, get) => ({
  toasts: [],
  addToast: (input) => {
    const id = Math.random().toString(36).slice(2, 9);
    const duration = input.duration ?? DEFAULT_DURATION;
    set((state) => ({
      toasts: [
        // Prepend so newest appears at the top of the stack
        { ...input, id, duration },
        // Enforce max visible toasts — drop oldest from the tail
        ...state.toasts.slice(0, MAX_TOASTS - 1)
      ]
    }));
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }
    return id;
  },
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id)
    }));
  },
  clearAll: () => {
    set({ toasts: [] });
  }
}));
var toastVariants = cva(
  // Base: card-like panel with logical border-start accent
  [
    "relative flex w-full items-start gap-3",
    "rounded-md border border-charcoal-100 bg-[var(--color-window-face)] p-4 shadow-md",
    // Inline-start colored accent strip using logical border
    "border-s-4",
    // Transition for enter/exit
    "transition-all duration-200"
  ],
  {
    variants: {
      variant: {
        success: "border-s-success",
        error: "border-s-error",
        warning: "border-s-warning",
        info: "border-s-info"
      }
    },
    defaultVariants: {
      variant: "info"
    }
  }
);
var VARIANT_ICON = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info
};
var VARIANT_ICON_CLASS = {
  success: "text-success",
  error: "text-error",
  warning: "text-warning",
  info: "text-info"
};
function ToastItem({ toast, onDismiss }) {
  const Icon = VARIANT_ICON[toast.variant];
  const iconClass = VARIANT_ICON_CLASS[toast.variant];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "alert",
      "aria-live": "assertive",
      "aria-atomic": "true",
      className: toastVariants({ variant: toast.variant }),
      children: [
        /* @__PURE__ */ jsx(
          Icon,
          {
            className: cn("mt-0.5 size-5 shrink-0", iconClass),
            "aria-hidden": "true"
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold text-charcoal-900 leading-snug", children: toast.title }),
          toast.description !== void 0 && /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-charcoal-500 leading-relaxed", children: toast.description })
        ] }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: () => onDismiss(toast.id),
            className: cn(
              "shrink-0 rounded-sm text-charcoal-400",
              "opacity-70 transition-opacity hover:opacity-100",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
            ),
            "aria-label": "Dismiss notification",
            children: /* @__PURE__ */ jsx(X, { className: "size-4", "aria-hidden": "true" })
          }
        )
      ]
    }
  );
}
function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);
  if (toasts.length === 0) return null;
  return /* @__PURE__ */ jsx(
    "div",
    {
      "aria-label": "Notifications",
      className: cn(
        // Position — logical end so it flips correctly in RTL
        "fixed bottom-4 end-4 z-50",
        // Stack layout
        "flex flex-col gap-2",
        // Constrain width on larger screens
        "w-full max-w-sm",
        // Pointer-events: allow clicks through the container gap
        "pointer-events-none"
      ),
      children: toasts.map((t) => /* @__PURE__ */ jsx("div", { className: "pointer-events-auto", children: /* @__PURE__ */ jsx(ToastItem, { toast: t, variant: t.variant, onDismiss: removeToast }) }, t.id))
    }
  );
}
function useToast() {
  const addToast = useToastStore((s) => s.addToast);
  const removeToast = useToastStore((s) => s.removeToast);
  const clearAll = useToastStore((s) => s.clearAll);
  const toast = React.useCallback(
    (input) => addToast(input),
    [addToast]
  );
  const dismiss = React.useCallback(
    (id) => removeToast(id),
    [removeToast]
  );
  return { toast, dismiss, clearAll };
}

export { Avatar, Badge, Button, DataTable, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EditableField, EmptyState, Input, LRI, Listbox, Markdown, PDI, PersonSelect, PickerSelect, RLI, Select, Skeleton, SkeletonTable, Textarea, ToastContainer, blockDirection, hasBidiControls, hasRtlLetters, initialsOf, isolateRuns, isolateText, oppositeRuns, stripBidiControls, textDirection, useToast, useToastStore };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map